import {
  FormDefinitionElement,
  FormField,
  FormResponseCustomField,
  ProcessedFormResponse,
} from '../types';

/**
 * Checks if field is of type multiselect group []
 */
export function isGroupField(field: string): boolean {
  return !!field && field.indexOf('[]') > -1;
}

/**
 * Checks if field is of type radio group
 */
export function isRadioGroupField(field: string): boolean {
  return field.indexOf('radio-group-') === 0;
}

/**
 * Processes label for fieldName, obtaining the clean text value
 */
export function processLabelValue(fieldName: string): string {
  return $(`label[for="${fieldName}"]`).children().remove().end().text().trim();
}

/**
 * Validates that all required checkbox and radio groups have at least one
 * selection. Returns an array of error messages (empty array means valid).
 */
export function validateCheckboxGroupAndRadioGroupRequired(
  formData: FormField[],
  formDefinition: FormDefinitionElement[],
): string[] {
  const errorMessages: string[] = [];

  const requiredGroups = formDefinition.filter(
    (element) =>
      (element.type === 'checkbox-group' || element.type === 'radio-group') && element.required,
  );

  const groupResponses = formData.filter(
    (data) =>
      data.name.includes('radio-group-') || data.name.includes('checkbox-group-'),
  );

  for (const currentGroup of requiredGroups) {
    const hasResponse = groupResponses.some(
      (response) => response.name.includes(currentGroup.name) && response.value !== '',
    );

    if (!hasResponse) {
      errorMessages.push(`Please select at least one option for: ${currentGroup.label}`);
    }
  }

  return errorMessages;
}

/**
 * Formats formResponse data. Joins any number of related group fields.
 */
export function processGroupField(data: FormField[]): FormField[] {
  const result: FormField[] = [];
  const collection: Record<string, FormField & { values?: string[] }> = {};

  for (const response of data) {
    let field = response.name;

    if (isGroupField(field)) {
      field = field.replace('[]', '');
      if (!collection[field]) {
        collection[field] = { name: response.name, value: '', values: [] };
      }

      if (response) {
        if (
          collection[field].values!.at(-1) === 'on' &&
          $(`#${field}-other-value`).length &&
          response.value == $(`#${field}-other-value`).val()
        ) {
          if ($(`input:checkbox[name='${field}[]']:last`).is(':checked')) {
            collection[field].values!.pop();
            collection[field].values!.push(`Other: ${response.value}`);
            collection[field].otherValue = response.value;
          }
        } else if (response.value.length > 0) {
          collection[field].values!.push(response.value);
        }
      }
    } else {
      const inputValue = { ...response };

      if (!collection[field]) {
        collection[field] = inputValue;
      } else if (collection[field].value === 'on') {
        collection[field].value = 'Other: ' + inputValue.value;
        collection[field].otherValue = inputValue.value;
      }

      if (
        /^checkbox/.test(field) &&
        !$('#' + field).attr('toggle') &&
        collection[field].value === 'on'
      ) {
        collection[field].value = 'checked';
      }
    }
  }

  for (const key in collection) {
    if (Object.prototype.hasOwnProperty.call(collection, key)) {
      const fieldObject: FormField = {
        value: collection[key].values ? collection[key].values!.join(', ') : collection[key].value,
        label: collection[key].label,
        name: collection[key].name,
        customFieldId: collection[key].customFieldId,
        customFieldLabel: collection[key].customFieldLabel,
        customFieldName: collection[key].customFieldName,
      };

      if (Object.prototype.hasOwnProperty.call(collection[key], 'otherValue')) {
        fieldObject.otherValue = collection[key].otherValue;
      }

      result.push(fieldObject);
    }
  }

  return result;
}

/**
 * Processes serialized form data into the shape required by the API.
 * Enriches each field with its label and custom-field metadata.
 */
export function processResponse(data: FormField[]): ProcessedFormResponse {
  const customFields: FormResponseCustomField[] = [];

  const processedData = processGroupField(data);

  for (let i = 0, ii = processedData.length; i < ii; i++) {
    let fieldName = processedData[i].name;
    let field: JQuery<HTMLElement>;

    if (isGroupField(fieldName)) {
      fieldName = fieldName.replace('[]', '');
      field = $(`#${fieldName}, #${fieldName}-0`);
    } else if (isRadioGroupField(fieldName)) {
      field = $(`input[type="radio"][name="${fieldName}"]:checked`);
    } else {
      field = $(`#${fieldName}`);
    }

    const label = processLabelValue(fieldName);
    const customField = field.attr('data-custom-field');

    if (customField && customField !== 'none') {
      const customFieldData = customField.split('%%');
      // customFieldData format: [id, name, label]
      customFields.push({
        id: customFieldData[0],
        name: customFieldData[1],
        label: customFieldData[2],
        fieldLabel: customFieldData[2],
        value: processedData[i].value,
        fieldValue: processedData[i].value,
      });
      processedData[i].customFieldLabel = customFieldData[2];
      processedData[i].customFieldName = customFieldData[1];
      processedData[i].customFieldId = customFieldData[0];
    }

    processedData[i].label = label;
  }

  return {
    responseData: { fields: processedData },
    customFields,
  };
}

